import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import ApiInspector from '../../components/ApiInspector';
import BopMultiSelect from '../../components/BopMultiSelect';
import { apiFetch } from '../../lib/api';
import { usePermesso } from '../../context/PermessiContext';
import { useChatAssistenza } from '../../context/ChatAssistenzaContext';
import {
  TICKET_TIPO_LABEL, TICKET_PRIORITA_LABEL, TICKET_STATO_LABEL, ticketStatoTesto,
  ticketStatoBadge, ticketPrioritaBadge,
  TicketTipo, TicketPriorita, TicketStato, TicketSummary,
} from './ticketAssistenza';
import { API_DOCS } from './RichiesteAssistenzaPage.api';
import './Assistenza.css';

const MODULO = 'assistenza.richieste';

export default function RichiesteAssistenzaPage() {
  const { can, gestisceAssistenza } = usePermesso();
  const canEdit = can(MODULO, 'edit');
  const navigate = useNavigate();
  const { apri: apriAssistente } = useChatAssistenza();

  const [items, setItems] = useState<TicketSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filtri a scelta multipla: lista vuota = nessun filtro.
  // All'apertura si vedono solo i ticket ancora da lavorare.
  // Default: tutto quello che non è chiuso. I risolti aspettano la conferma di
  // chi ha aperto il ticket, quindi sono i più urgenti da vedere, non i finiti.
  const [filtroStato, setFiltroStato] = useState<TicketStato[]>(['APERTO', 'IN_LAVORAZIONE', 'RISOLTO']);
  const [filtroTipo, setFiltroTipo] = useState<TicketTipo[]>([]);
  const [filtroPriorita, setFiltroPriorita] = useState<TicketPriorita[]>([]);
  const [ricerca, setRicerca] = useState('');


  const load = async () => {
    setLoading(true);
    setError('');
    try {
      // @api GET /api/assistenza/ticket -> { success: true, data: [{ id, tipo, priorita, stato, titolo, modulo, urlPagina, createdByName, createdAt, societa: { id, nome }, _count: { allegati, risposte } }] }
      const res = await apiFetch<{ success: boolean; data: TicketSummary[] }>('/api/assistenza/ticket');
      setItems(res.data ?? []);
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = ricerca.trim().toLowerCase();
    return items.filter(t => {
      if (filtroStato.length > 0 && !filtroStato.includes(t.stato)) return false;
      if (filtroTipo.length > 0 && !filtroTipo.includes(t.tipo)) return false;
      if (filtroPriorita.length > 0 && !filtroPriorita.includes(t.priorita)) return false;
      if (q && !(
        t.titolo.toLowerCase().includes(q) ||
        (t.modulo || '').toLowerCase().includes(q) ||
        (t.createdByName || '').toLowerCase().includes(q)
      )) return false;
      return true;
    });
  }, [items, ricerca, filtroStato, filtroTipo, filtroPriorita]);

  // Il ticket lo crea il pannello dell'assistente (o la modale classica di ripiego):
  // @api POST /api/assistenza/ticket FormData{ tipo, priorita, titolo, descrizione, modulo?, urlPagina?, societaId?, chatId?, allegati[]? } -> { success: true, data: { id } }

  return (
    <Layout>
      <div className="assistenza-richieste">
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2>Richieste assistenza</h2>
            <p>
              {gestisceAssistenza
                ? 'Tutte le richieste di assistenza aperte dagli utenti.'
                : 'Apri una richiesta di miglioria, segnala un errore o chiedi supporto. Vedi solo le tue richieste.'}
            </p>
          </div>
          {canEdit && (
            <button className="bop-btn bop-btn-primary" onClick={() => apriAssistente({ urlPagina: window.location.href })}>+ Nuova richiesta</button>
          )}
        </div>

        <div className="bop-filter-bar">
          <input
            className="bop-input assist-search"
            placeholder="Cerca per titolo, modulo o utente…"
            value={ricerca}
            onChange={e => setRicerca(e.target.value)}
          />
          <BopMultiSelect
            label="Stato"
            tuttiLabel="Tutti gli stati"
            items={(Object.keys(TICKET_STATO_LABEL) as TicketStato[]).map(s => ({ id: s, nome: ticketStatoTesto(s, gestisceAssistenza) }))}
            value={filtroStato}
            onChange={v => setFiltroStato(v as TicketStato[])}
          />
          <BopMultiSelect
            label="Tipo"
            tuttiLabel="Tutti i tipi"
            items={(Object.keys(TICKET_TIPO_LABEL) as TicketTipo[]).map(t => ({ id: t, nome: TICKET_TIPO_LABEL[t] }))}
            value={filtroTipo}
            onChange={v => setFiltroTipo(v as TicketTipo[])}
          />
          <BopMultiSelect
            label="Priorità"
            tuttiLabel="Tutte le priorità"
            items={(Object.keys(TICKET_PRIORITA_LABEL) as TicketPriorita[]).map(p => ({ id: p, nome: TICKET_PRIORITA_LABEL[p] }))}
            value={filtroPriorita}
            onChange={v => setFiltroPriorita(v as TicketPriorita[])}
          />
        </div>

        {error && <div className="bop-inline-error">{error}</div>}

        <div className="bop-card">
          <table className="bop-table bop-table--clickable">
            <thead>
              <tr>
                <th className="assist-col-id">#</th>
                <th>Titolo</th>
                <th className="assist-col-tipo">Tipo</th>
                <th className="assist-col-prio">Priorità</th>
                <th className="assist-col-stato">Stato</th>
                {gestisceAssistenza && <th>Utente</th>}
                <th className="assist-col-data">Apertura</th>
                <th className="bop-text-center assist-col-meta">Allegati</th>
                <th className="bop-text-center assist-col-meta">Risposte</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={gestisceAssistenza ? 9 : 8}><div className="bop-empty-state">Caricamento…</div></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={gestisceAssistenza ? 9 : 8}>
                  <div className="bop-empty-state">
                    {items.length === 0 ? 'Nessuna richiesta presente.' : 'Nessun risultato per i filtri impostati.'}
                  </div>
                </td></tr>
              ) : filtered.map(t => (
                <tr key={t.id} onClick={() => navigate(`/assistenza/richieste/${t.id}`)}>
                  <td className="assist-col-id">{t.id}</td>
                  <td>
                    <div className="assist-titolo-cell">
                      <span className="assist-titolo">{t.titolo}</span>
                      {t.modulo && <span className="bop-text-muted assist-modulo">{t.modulo}</span>}
                    </div>
                  </td>
                  <td>{TICKET_TIPO_LABEL[t.tipo]}</td>
                  <td><span className={`bop-badge ${ticketPrioritaBadge(t.priorita)}`}>{TICKET_PRIORITA_LABEL[t.priorita]}</span></td>
                  <td><span className={`bop-badge ${ticketStatoBadge(t.stato)}`}>{ticketStatoTesto(t.stato, gestisceAssistenza)}</span></td>
                  {gestisceAssistenza && <td>{t.createdByName}</td>}
                  <td>{new Date(t.createdAt).toLocaleDateString('it-IT')}</td>
                  <td className="bop-text-center">{t._count?.allegati || 0}</td>
                  <td className="bop-text-center">{t._count?.risposte || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
